import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Form, Input, Radio, Row, Select } from 'antd';
import { useDispatch } from 'react-redux';
import { formRules, requireText } from 'common/const/common.const';
import { ImageUpload } from 'common/components/Image/ImageUpload';
import { useFormMapper } from 'common/helpers/form.helper';
import { ConfirmationModal } from 'common/components/ConfirmationModal';
import { useAppSelector } from 'app/store/store.hooks';
import { setMenuItemModelError, setMenuItemModelParams } from 'app/store/reducers/menuItem.reducer';
import { history } from 'app/store';
import { CategorySelector } from 'entities/Category/components/Selector/CategorySelector';
import { MealBuilderItemSelect } from 'entities/MenuItem/components/Selector/MealBuilderItemSelect';
import {
  EMenuItemDrinkOptions,
  EMenuItemDrinkOptionsText,
  EMenuItemProductType,
  EMenuItemProductTypeText,
  IMenuItemCreateData,
  IMenuItemUpdateData,
} from 'entities/MenuItem/MenuItem.models';
import { ECommonEntityStatus, ECommonEntityStatusText } from 'entities/Bitecoin/Bitecoin.models';

interface IComponentProps {
  createMenuItem?: (data: IMenuItemCreateData) => void;
  updateMenuItem?: (data: IMenuItemUpdateData) => void;
  menuItemId?: string;
}

export const MenuItemForm: React.FC<IComponentProps> = (props) => {
  const dispatch = useDispatch();
  const { menuItemModel, menuItemModelLoading, menuItemModelParams, menuItemModelError } = useAppSelector(
    (state) => state.menuItem
  );
  const { createMenuItem, updateMenuItem, menuItemId } = props;
  const isUpdateForm = !!menuItemId;
  const submitButtonText = isUpdateForm ? 'Save' : ' Add';

  const [form] = Form.useForm();

  const { fields, setField } = useFormMapper(
    [
      'categoryData',
      'displayCategoryData',
      'name',
      'description',
      'image',
      'status',
      'productType',
      'includesDrink',
      'drinkQty',
      'drinkOptions',
      'includesSides',
      'sidesQty',
      'defaultDrinkId',
      'defaultSideId',
    ],
    menuItemModel,
    menuItemModelParams,
    menuItemModelError
  );

  const normalizedFields = useMemo(() => {
    return fields.map((field) =>
      field.name === 'drinkQty' || field.name === 'sidesQty' ? { ...field, value: 1 } : field
    );
  }, [fields]);

  // Tracks whether the user has manually edited the display category — once true, we stop
  // auto-filling it from the permanent category.
  const displayTouchedRef = useRef(false);

  const onValuesChange = (changedValues: any) => {
    if ('displayCategoryData' in changedValues) {
      displayTouchedRef.current = true;
    }
    if ('categoryData' in changedValues) {
      const permanent = changedValues.categoryData;
      setField({ name: 'categoryData', value: permanent });
      if (permanent?.isMealBuilder === true) {
        setField({ name: 'productType', value: EMenuItemProductType.Other });
      }
      // The display category must mirror the permanent category's add-ons setting (type) and Meal
      // Builder flag. Auto-fill display = permanent until the user hand-picks a matching display
      // category; also re-fill when changing the permanent category makes the current pick invalid.
      const currentDisplay = form.getFieldValue('displayCategoryData');
      const displayMatches =
        !!currentDisplay &&
        currentDisplay.type === permanent?.type &&
        currentDisplay.isMealBuilder === permanent?.isMealBuilder;
      if (!displayTouchedRef.current || !displayMatches) {
        displayTouchedRef.current = false;
        setField({ name: 'displayCategoryData', value: permanent });
      }
    }
    // Drink temperature changed → the previously chosen default drink may no longer be valid; clear it.
    if ('drinkOptions' in changedValues) {
      form.setFieldValue('defaultDrinkId', null);
    }
  };

  const [confirmationModalData, setConfirmationModalData] = useState<{ isVisible: boolean; data?: any }>({
    isVisible: false,
  });

  const showModal = (data?: any) => setConfirmationModalData({ isVisible: true, data });
  const hideModal = () => setConfirmationModalData({ isVisible: false, data: null });

  const addMenuItem = (values: any) => {
    const {
      name,
      description,
      categoryData,
      displayCategoryData,
      image,
      status,
      productType,
      includesDrink,
      drinkQty,
      drinkOptions,
      includesSides,
      sidesQty,
      defaultDrinkId,
      defaultSideId,
    } = values;
    const isCombo = productType === EMenuItemProductType.Combo;
    const newData: IMenuItemCreateData = {
      name,
      description,
      image,
      category: categoryData?.id,
      displayCategory: displayCategoryData?.id,
      status,
      productType,
      includesDrink,
      drinkQty,
      drinkOptions,
      includesSides,
      sidesQty,
      // Send null (not undefined) to clear — an unset default keeps the combo hidden from the menu.
      defaultDrinkId: isCombo && includesDrink ? defaultDrinkId ?? null : null,
      defaultSideId: isCombo && includesSides ? defaultSideId ?? null : null,
    };

    createMenuItem && createMenuItem(newData);
  };

  const editMenuItem = (values?: any) => {
    const updateMenuItemData = values ? values : confirmationModalData.data || null;

    if (!updateMenuItemData) {
      return;
    }

    const {
      name,
      description,
      image,
      status,
      categoryData,
      displayCategoryData,
      productType,
      includesDrink,
      drinkQty,
      drinkOptions,
      includesSides,
      sidesQty,
      defaultDrinkId,
      defaultSideId,
    } = updateMenuItemData;

    if (menuItemId && updateMenuItem) {
      const isCombo = productType === EMenuItemProductType.Combo;
      const newData: IMenuItemUpdateData = {
        id: menuItemId,
        name,
        description,
        image,
        status,
        category: categoryData.id,
        displayCategory: displayCategoryData?.id,
        productType,
        includesDrink,
        drinkQty,
        drinkOptions,
        includesSides,
        sidesQty,
        // Send null (not undefined) to clear — an unset default keeps the combo hidden from the menu.
        defaultDrinkId: isCombo && includesDrink ? defaultDrinkId ?? null : null,
        defaultSideId: isCombo && includesSides ? defaultSideId ?? null : null,
      };

      updateMenuItem(newData);
    }
  };

  const onSubmit = (values: any) => {
    if (isUpdateForm) {
      if (values.status === ECommonEntityStatus.Archived) {
        showModal(values);
      } else {
        editMenuItem(values);
      }
    } else {
      addMenuItem(values);
    }
  };

  const clearFormData = () => {
    dispatch(setMenuItemModelParams(null));
    dispatch(setMenuItemModelError(null));
  };

  const onCancel = () => {
    clearFormData();

    history.go(-1);
  };

  useEffect(() => {
    return () => {
      clearFormData();
    };
  }, []);

  return (
    <>
      <ConfirmationModal
        isVisible={confirmationModalData.isVisible}
        title="Are you sure you want to archive this item?"
        mainText="It will be hidden from all active menus and default views."
        onSubmit={() => editMenuItem()}
        onCancel={hideModal}
        submitText="Yes, Archive"
        loading={menuItemModelLoading}
      />

      <Form
        form={form}
        className="mt-5"
        onFinish={onSubmit}
        fields={normalizedFields}
        layout="vertical"
        onValuesChange={onValuesChange}
      >
        <Row gutter={[28, 0]}>
          <Col span={12}>
            <Form.Item rules={[formRules.required]} name="categoryData" label="Permanent Category">
              <CategorySelector placeholder="Choose permanent category" disabled={menuItemModelLoading || isUpdateForm} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.categoryData !== currentValues.categoryData}
            >
              {({ getFieldValue }) => {
                const permanent = getFieldValue('categoryData');
                return (
                  <Form.Item rules={[formRules.required]} name="displayCategoryData" label="Display category">
                    <CategorySelector
                      placeholder="Choose display category"
                      disabled={menuItemModelLoading}
                      type={permanent?.type}
                      isMealBuilder={permanent?.isMealBuilder}
                      mealBuilderRole={permanent?.mealBuilderRole}
                      drinkTemperature={permanent?.drinkTemperature}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                formRules.required,
                {
                  min: 2,
                  message: 'Item name must be longer than or equal 2 characters',
                },
                {
                  max: 100,
                  message: 'Item name must be shorter than or equal 100 characters',
                },
              ]}
              name="name"
              label="Item name"
            >
              <Input placeholder="Item name" name="name" type="text" disabled={menuItemModelLoading} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.categoryData !== currentValues.categoryData}
            >
              {({ getFieldValue }) => {
                const isMealBuilder = getFieldValue('categoryData')?.isMealBuilder === true;
                return (
                  <Form.Item rules={[{ required: true, message: requireText }]} name="productType" label="Product type">
                    <Select
                      placeholder="Choose product type"
                      className="width-full"
                      disabled={isMealBuilder || menuItemModelLoading || isUpdateForm}
                    >
                      <Select.Option
                        value={EMenuItemProductType.SandwichOnly}
                        label={EMenuItemProductTypeText.SandwichOnly}
                      >
                        {EMenuItemProductTypeText.SandwichOnly}
                      </Select.Option>
                      <Select.Option value={EMenuItemProductType.Combo} label={EMenuItemProductTypeText.Combo}>
                        {EMenuItemProductTypeText.Combo}
                      </Select.Option>
                      <Select.Option value={EMenuItemProductType.Bundle} label={EMenuItemProductTypeText.Bundle}>
                        {EMenuItemProductTypeText.Bundle}
                      </Select.Option>
                      <Select.Option value={EMenuItemProductType.Other} label={EMenuItemProductTypeText.Other}>
                        {EMenuItemProductTypeText.Other}
                      </Select.Option>
                    </Select>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: requireText,
                },
              ]}
              label="Item status"
              name="status"
            >
              <Select className="width-full">
                <Select.Option value={ECommonEntityStatus.Active} label={ECommonEntityStatusText.Active}>
                  {ECommonEntityStatusText.Active}
                </Select.Option>
                <Select.Option value={ECommonEntityStatus.Inactive} label={ECommonEntityStatusText.Inactive}>
                  {ECommonEntityStatusText.Inactive}
                </Select.Option>
                <Select.Option value={ECommonEntityStatus.Archived} label={ECommonEntityStatusText.Archived}>
                  {ECommonEntityStatusText.Archived}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              rules={[
                {
                  min: 2,
                  message: 'Description must be longer than or equal 2 characters',
                },
                {
                  max: 400,
                  message: 'Description must be shorter than or equal 400 characters',
                },
              ]}
              name="description"
              label="Description"
            >
              <Input.TextArea placeholder="Description" name="description" disabled={menuItemModelLoading} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item rules={[formRules.required]} name="image" label='Image (recommended dimensions: "800px x 800px")'>
          <ImageUpload />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.productType !== currentValues.productType}
        >
          {({ getFieldValue }) =>
            getFieldValue('productType') === EMenuItemProductType.Combo ? (
              <>
                <Form.Item
                  rules={[formRules.required]}
                  name="includesDrink"
                  label="Item includes drink"
                  preserve={false}
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.includesDrink !== currentValues.includesDrink}
                >
                  {({ getFieldValue }) =>
                    getFieldValue('includesDrink') === true ? (
                      <>
                        <Row>
                          <Col span={12}>
                            <Form.Item rules={[formRules.required]} name="drinkQty" label="Drink quantity">
                              <Input type="number" disabled />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={12}>
                            <Form.Item
                              rules={[formRules.required]}
                              name="drinkOptions"
                              label="Drink options"
                              preserve={false}
                            >
                              <Select placeholder="Choose drink options" className="width-full">
                                <Select.Option value={EMenuItemDrinkOptions.Hot} label={EMenuItemDrinkOptionsText.Hot}>
                                  {EMenuItemDrinkOptionsText.Hot}
                                </Select.Option>
                                <Select.Option
                                  value={EMenuItemDrinkOptions.Cold}
                                  label={EMenuItemDrinkOptionsText.Cold}
                                >
                                  {EMenuItemDrinkOptionsText.Cold}
                                </Select.Option>
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item
                          noStyle
                          shouldUpdate={(prevValues, currentValues) =>
                            prevValues.drinkOptions !== currentValues.drinkOptions
                          }
                        >
                          {({ getFieldValue }) => (
                            <Row>
                              <Col span={12}>
                                <Form.Item
                                  name="defaultDrinkId"
                                  label="Default drink"
                                  preserve={false}
                                  extra="Leave empty to hide this combo from the menu"
                                >
                                  <MealBuilderItemSelect
                                    role="drink"
                                    drinkTemperature={getFieldValue('drinkOptions')}
                                    placeholder="Choose default drink"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          )}
                        </Form.Item>
                      </>
                    ) : null
                  }
                </Form.Item>
                <Form.Item
                  rules={[formRules.required]}
                  name="includesSides"
                  label="Item includes sides"
                  preserve={false}
                >
                  <Radio.Group>
                    <Radio value={true}>Yes</Radio>
                    <Radio value={false}>No</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) => prevValues.includesSides !== currentValues.includesSides}
                >
                  {({ getFieldValue }) =>
                    getFieldValue('includesSides') === true ? (
                      <>
                        <Row>
                          <Col span={12}>
                            <Form.Item rules={[formRules.required]} name="sidesQty" label="Sides quantity">
                              <Input type="number" disabled />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row>
                          <Col span={12}>
                            <Form.Item
                              name="defaultSideId"
                              label="Default side"
                              preserve={false}
                              extra="Leave empty to hide this combo from the menu"
                            >
                              <MealBuilderItemSelect role="side" placeholder="Choose default side" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </>
                    ) : null
                  }
                </Form.Item>
              </>
            ) : null
          }
        </Form.Item>

        <Row justify="space-between" align="middle">
          <Col span={16}>
            <Form.Item className="mb-0">
              <Button
                className="mr-8"
                htmlType="submit"
                type="primary"
                disabled={menuItemModelLoading}
                loading={menuItemModelLoading}
                title={submitButtonText}
              >
                {submitButtonText}
              </Button>
              <Button onClick={onCancel} disabled={menuItemModelLoading} loading={menuItemModelLoading} title="Cancel">
                Cancel
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};
